/**
 * Cart Complementary Recommendations System
 * Uses Shopify's Search & Discovery API for complementary products
 * Queries fresh data on every cart change
 */

class CartComplementaryRecommendations {
  constructor() {
    this.abortController = null;
    this.isLoading = false;
    this.lastLoadTime = 0;
    this.minLoadInterval = 500; // Minimum 500ms between loads
    this.lastCartHash = null; // Track cart state to avoid unnecessary reloads
    this.recommendationsLoaded = false; // Track if recommendations have been loaded
    this.loadTimeout = null; // Track timeout for fallback

    console.log('🎯 Cart Complementary Recommendations initialized');
  }

  /**
   * Generate a hash of cart state to detect changes
   */
  getCartHash(cartItems) {
    if (!cartItems || cartItems.length === 0) {
      return 'empty';
    }

    // Ignore pure gift items so toggling gifts does not trigger
    // recommendation reloads. This includes product-level gifts
    // (marked with _product_gift) and order-level gifts
    // (marked with _gift_order).
    const relevantItems = cartItems.filter((item) => {
      if (!item.properties) return true;

      const isProductGift = item.properties._product_gift === 'true';
      const isOrderGift = item.properties._gift_order === 'true';

      return !isProductGift && !isOrderGift;
    });

    if (relevantItems.length === 0) {
      return 'gift-only';
    }

    // Create hash from product IDs and quantities of non-gift items
    return relevantItems
      .map((item) => `${item.product_id}:${item.quantity}`)
      .sort()
      .join('|');
  }

  /**
   * Fetch complementary products for a single product
   */
  async fetchComplementaryProducts(productId, limit = 10) {
    try {
      const url = `/recommendations/products.json?product_id=${productId}&limit=${limit}&intent=complementary`;
      const response = await fetch(url, {
        signal: this.abortController?.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      return data.products || [];
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log(`🚫 Request aborted for product ${productId}`);
      } else {
        console.warn(`⚠️ Failed to fetch complementary for ${productId}:`, error.message);
      }
      return [];
    }
  }

  /**
   * Get unique product IDs from cart items
   */
  getUniqueCartProductIds(cartItems) {
    return [...new Set(cartItems.map((item) => item.product_id))];
  }

  /**
   * Build aggregated recommendations from all cart items
   */
  async buildRecommendations(cartItems) {
    const startTime = performance.now();

    // Get unique product IDs from cart
    const uniqueProductIds = this.getUniqueCartProductIds(cartItems);
    const cartProductIdSet = new Set(uniqueProductIds);

    console.log(`🛒 Building recommendations for ${uniqueProductIds.length} unique cart items`);

    // Fetch complementary products for each cart item (no caching)
    const fetchPromises = uniqueProductIds.map(async (productId) => {
      const products = await this.fetchComplementaryProducts(productId);
      return { productId, products };
    });

    const results = await Promise.all(fetchPromises);

    // Aggregate all complementary products
    const aggregatedProducts = [];
    const seenProductIds = new Set(cartProductIdSet);

    results.forEach(({ productId, products }) => {
      console.log(`  Product ${productId}: ${products.length} complementary items`);

      products.forEach((product) => {
        if (!seenProductIds.has(product.id)) {
          aggregatedProducts.push(product);
          seenProductIds.add(product.id);
        }
      });
    });

    const loadTime = performance.now() - startTime;
    console.log(`✅ Found ${aggregatedProducts.length} unique recommendations in ${Math.round(loadTime)}ms`);

    return {
      products: aggregatedProducts, // Return all products, no limit
      totalFound: aggregatedProducts.length,
      loadTime: Math.round(loadTime),
    };
  }

  /**
   * Load and render recommendations for current cart
   */
  async loadRecommendations(providedCartData = null, forceReload = false) {
    // Prevent rapid successive calls
    const now = Date.now();
    if (this.isLoading) {
      console.log('⏳ Already loading recommendations, skipping...');
      return;
    }

    if (now - this.lastLoadTime < this.minLoadInterval) {
      console.log('⏳ Too soon since last load, skipping...');
      return;
    }

    this.isLoading = true;
    this.lastLoadTime = now;

    // Abort any previous request
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    // Clear any existing timeout
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout);
      this.loadTimeout = null;
    }

    let timeoutId = null;

    try {
      const miniCollection = document.querySelector('.cart-drawer__mini-collection');
      const container = document.querySelector('.cart-drawer__mini-collection-products');

      if (!container || !miniCollection) {
        console.warn('⚠️ Recommendations container not found');
        this.isLoading = false;
        return;
      }

      // Check if this is for empty or filled cart
      const cartState = miniCollection.getAttribute('data-cart-state');
      const recommendationType = miniCollection.getAttribute('data-recommendation-type');

      console.log(`🔍 Mini collection state: cartState="${cartState}", recommendationType="${recommendationType}"`);

      // Only proceed if cart_aware_recommendations is selected
      if (recommendationType !== 'cart_aware_recommendations') {
        console.log('❌ Not cart-aware recommendations type, skipping dynamic load');
        this.isLoading = false;
        return;
      }

      // Get cart data
      let cartData;
      if (providedCartData) {
        cartData = providedCartData;
        console.log('📦 Using provided cart data');
      } else {
        const response = await fetch('/cart.js', {
          signal: this.abortController.signal,
        });
        cartData = await response.json();
        console.log('📦 Fetched cart data');
      }

      const cartItems = cartData.items || [];

      console.log(`📊 Cart has ${cartItems.length} items`);

      // For empty cart, skip dynamic recommendations completely
      if (cartItems.length === 0) {
        console.log('✋ Cart is empty - skipping dynamic recommendations (static collection should be shown)');
        this.recommendationsLoaded = true;
        this.isLoading = false;
        return;
      }

      // Check if cart state has changed
      const currentCartHash = this.getCartHash(cartItems);
      if (!forceReload && this.lastCartHash === currentCartHash && this.recommendationsLoaded) {
        console.log('📌 Cart state unchanged, skipping reload');
        this.isLoading = false;
        return;
      }

      // Update cart hash
      this.lastCartHash = currentCartHash;

      // Show loading state
      container.innerHTML = `
        <div class="cart-drawer__recommendations-loading">
          <div class="cart-drawer__recommendations-spinner">
            <svg class="spinner" viewBox="0 0 50 50" width="40" height="40">
              <circle class="path" cx="25" cy="25" r="20" fill="none" stroke="#dc600e" stroke-width="4"></circle>
            </svg>
          </div>
          <p>Loading recommendations...</p>
        </div>
      `;

      // Set up timeout fallback (8 seconds)
      timeoutId = setTimeout(() => {
        if (this.isLoading) {
          console.warn('⏱️ Recommendations loading timeout (8s), falling back to static collection');
          this.loadStaticCollectionFallback(container, miniCollection);
          this.isLoading = false;
          this.loadTimeout = null;
        }
      }, 8000);
      this.loadTimeout = timeoutId;

      // Build recommendations (no limit, load all)
      const result = await this.buildRecommendations(cartItems);

      // Clear timeout if recommendations loaded successfully
      clearTimeout(timeoutId);

      if (result.products.length === 0) {
        console.log('No complementary products found, loading static collection fallback');
        await this.loadStaticCollectionFallback(container, miniCollection);
        this.recommendationsLoaded = true; // Mark as loaded to prevent infinite loop
        this.isLoading = false;
        return;
      }

      // Render products using card-product template structure
      await this.renderProducts(result.products, container);
      this.recommendationsLoaded = true;
    } catch (error) {
      // Clear timeout on error
      if (timeoutId) {
        clearTimeout(timeoutId);
        this.loadTimeout = null;
      }

      if (error.name === 'AbortError') {
        console.log('🚫 Recommendations load aborted');
      } else {
        console.error('❌ Error loading recommendations:', error);
        const container = document.querySelector('.cart-drawer__mini-collection-products');
        const miniCollection = document.querySelector('.cart-drawer__mini-collection');
        if (container && miniCollection) {
          // Try to load static collection as fallback
          await this.loadStaticCollectionFallback(container, miniCollection);
        }
      }
    } finally {
      // Clear timeout in finally block as well
      if (timeoutId) {
        clearTimeout(timeoutId);
        this.loadTimeout = null;
      }
      this.isLoading = false;
    }
  }

  /**
   * Load static collection as fallback
   */
  async loadStaticCollectionFallback(container, miniCollection) {
    try {
      const collectionHandle = miniCollection.getAttribute('data-collection-handle');

      if (!collectionHandle || collectionHandle.trim() === '') {
        console.warn('⚠️ No collection handle found for fallback - keeping empty state');
        container.innerHTML =
          '<p style="text-align: center; padding: 2rem; color: #999;">No recommendations available</p>';
        return;
      }

      console.log(`📦 Loading static collection fallback: ${collectionHandle}`);

      // Fetch products from the collection directly
      const response = await fetch(`/collections/${collectionHandle}/products.json?limit=10`, {
        signal: this.abortController?.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const products = data.products || [];

      if (products.length > 0) {
        console.log(`✅ Loaded ${products.length} products from static collection`);
        await this.renderProducts(products, container, true); // Pass true for isStaticCollection
      } else {
        console.warn('⚠️ Static collection has no products');
        container.innerHTML =
          '<p style="text-align: center; padding: 2rem; color: #999;">No products available in this collection</p>';
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('🚫 Static collection load aborted');
      } else {
        console.error('❌ Failed to load static collection fallback:', error);
        container.innerHTML =
          '<p style="text-align: center; padding: 2rem; color: #999;">Unable to load recommendations</p>';
      }
    }
  }

  /**
   * Render products using card-product template structure
   */
  async renderProducts(products, container, isStaticCollection = false) {
    // Render products matching the card-product template
    this.renderProductCards(products, container, isStaticCollection);
  }

  /**
   * Normalize price value - handle different API formats
   * Recommendations API returns prices in cents/paise
   * Collections API returns prices already in rupees
   */
  normalizePrice(price, isStaticCollection = false) {
    if (!price) return '0.00';
    const numPrice = parseFloat(price);

    // Static collection API returns prices already in rupees/dollars
    if (isStaticCollection) {
      return numPrice.toFixed(2);
    }

    // Recommendations API returns prices in cents - divide by 100
    return (numPrice / 100).toFixed(2);
  }

  /**
   * Get product image URL - handle different API response formats
   */
  getProductImage(product) {
    // Try different image property formats
    if (product.featured_image) {
      return product.featured_image;
    }
    if (product.image && typeof product.image === 'string') {
      return product.image;
    }
    if (product.image && product.image.src) {
      return product.image.src;
    }
    if (product.images && product.images.length > 0) {
      const firstImage = product.images[0];
      return typeof firstImage === 'string' ? firstImage : firstImage.src;
    }
    return null;
  }

  /**
   * Render product cards matching the card-product template structure
   */
  renderProductCards(products, container, isStaticCollection = false) {
    const cardsHTML = products
      .map((product) => {
        const firstVariant = product.variants?.[0];

        // Use normalizePrice for proper price handling
        const price = firstVariant ? this.normalizePrice(firstVariant.price, isStaticCollection) : '0.00';
        const comparePrice = firstVariant?.compare_at_price
          ? this.normalizePrice(firstVariant.compare_at_price, isStaticCollection)
          : null;

        // Use getProductImage for proper image handling
        const image = this.getProductImage(product);
        const available = product.available !== false && firstVariant?.available !== false;

        // Build tags
        let tagsHTML = '';
        if (!available) {
          tagsHTML = '<div class="card__tags"><span class="badge card__tag card__tag--sold-out">Sold Out</span></div>';
        }

        // Format prices using Indian number formatting
        const formattedPrice = window.formatIndianPrice ? window.formatIndianPrice(price) : price;
        const formattedComparePrice =
          comparePrice && window.formatIndianPrice ? window.formatIndianPrice(comparePrice) : comparePrice;

        // Build price HTML matching card-price.liquid structure
        let priceHTML = '';
        if (comparePrice && parseFloat(comparePrice) > parseFloat(price)) {
          // On sale - show compare price and sale price
          priceHTML = `
            <div class="price cart-price price--on-sale">
              <div class="price__container">
                <div class="price__regular" style="display: none;">
                  <span class="price-item price-item--regular">₹${formattedPrice}</span>
                </div>
                <div class="price__sale">
                  <div class="price__sale-row">
                    <span style="color: #292929 !important;">
                      <s class="price-item price-item--regular" style="color: #292929 !important; text-decoration: line-through !important; font-weight: 400 !important; font-size: 0.77rem !important;">
                        ₹${formattedComparePrice}
                      </s>
                    </span>
                  </div>
                  <span class="price-item price-item--sale price-item--last">₹${formattedPrice}</span>
                </div>
              </div>
            </div>
          `;
        } else {
          // Regular price - ensure it's always visible
          priceHTML = `
            <div class="price cart-price">
              <div class="price__container">
                <div class="price__regular">
                  <span class="price-item price-item--regular" style="display: inline-block !important;">₹${formattedPrice}</span>
                </div>
              </div>
            </div>
          `;
        }

        return `
        <div class="cart-drawer__mini-collection-item">
          <div class="card-wrapper product-card-wrapper underline-links-hover">
            <div class="card card--standard card--media" style="--ratio-percent: 100%;">
              <h2 class="card__heading h5">
                <a href="/products/${product.handle}" class="full-unstyled-link">
                  ${product.title}
                </a>
              </h2>
              
              ${tagsHTML}
              
              <div class="card-image-container">
                ${
                  image
                    ? `
                  <a href="/products/${product.handle}" class="full-unstyled-link card-image-link">
                    <img
                      src="${image}"
                      alt="${product.title}"
                      class="card-image"
                      loading="lazy"
                      width="300"
                      height="300"
                    >
                  </a>
                `
                    : `
                  <div class="card-image-placeholder">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525 525" class="placeholder-svg">
                      <rect width="525" height="525" fill="#f5f5f5"/>
                    </svg>
                  </div>
                `
                }
              </div>
              
              <div class="card__content">
                <div class="card__information">
                  <div class="card-information">
                    <div class="price-and-cart-row">
                      ${priceHTML}
                      
                      ${
                        available && firstVariant
                          ? `
                        <div class="quick-add no-js-hidden">
                          <form method="post" action="/cart/add" class="cart-rec-form" data-product-id="${product.id}">
                            <input type="hidden" name="id" value="${firstVariant.id}">
                            <input type="hidden" name="quantity" value="1">
                            <button
                              type="submit"
                              name="add"
                              class="quick-add__submit button button--secondary"
                              aria-label="Add ${product.title} to cart"
                            >
                              <svg class="plus-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M10 4V16M4 10H16" stroke="#dc600e" stroke-width="2" stroke-linecap="round"/>
                              </svg>
                              <div class="loading__spinner hidden">
                                <svg class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                                  <circle class="path" fill="none" stroke-width="6" stroke-linecap="round" cx="33" cy="33" r="30"></circle>
                                </svg>
                              </div>
                            </button>
                          </form>
                        </div>
                      `
                          : ''
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      `;
      })
      .join('');

    container.innerHTML = cardsHTML;
    this.initializeQuickAdd(container);
    this.updateNavigationArrows();
  }

  /**
   * Update navigation arrow visibility based on scroll position
   */
  updateNavigationArrows() {
    const scrollContainer = document.querySelector('.cart-drawer__mini-collection-scroll');
    const prevButton = document.querySelector('.cart-drawer__mini-collection-nav--prev');
    const nextButton = document.querySelector('.cart-drawer__mini-collection-nav--next');

    if (!scrollContainer || !prevButton || !nextButton) return;

    const updateArrows = () => {
      const scrollLeft = scrollContainer.scrollLeft;
      const maxScroll = scrollContainer.scrollWidth - scrollContainer.clientWidth;

      // Show/hide prev button
      if (scrollLeft > 10) {
        prevButton.classList.add('visible');
      } else {
        prevButton.classList.remove('visible');
      }

      // Show/hide next button
      if (scrollLeft < maxScroll - 10) {
        nextButton.classList.add('visible');
      } else {
        nextButton.classList.remove('visible');
      }
    };

    // Initial check
    setTimeout(updateArrows, 100);

    // Update on scroll
    scrollContainer.addEventListener('scroll', updateArrows);
  }

  /**
   * Initialize quick-add functionality for rendered products
   */
  initializeQuickAdd(container) {
    const forms = container.querySelectorAll('form.cart-rec-form');

    forms.forEach((form) => {
      // Remove old event listeners by cloning the form element
      // This ensures we don't have duplicate listeners after DOM updates
      const newForm = form.cloneNode(true);
      form.parentNode.replaceChild(newForm, form);

      const handleSubmit = async (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();

        console.log('🛒 [CART-REC] Form submit handler called');

        const button = newForm.querySelector('button[type="submit"]');
        const plusIcon = button.querySelector('.plus-icon');
        const spinner = button.querySelector('.loading__spinner');

        // Prevent double submission
        if (button.disabled || button.dataset.submitting === 'true') {
          console.warn('⚠️ [CART-REC] Button already submitting, ignoring duplicate');
          return;
        }

        const variantId = newForm.querySelector('input[name="id"]').value;
        const quantity = newForm.querySelector('input[name="quantity"]').value;
        console.log(`🛒 [CART-REC] Adding variant ${variantId}, quantity: ${quantity}`);

        button.dataset.submitting = 'true';
        button.disabled = true;

        // Show spinner, hide plus icon
        if (plusIcon) plusIcon.style.display = 'none';
        if (spinner) spinner.classList.remove('hidden');

        try {
          const formData = new FormData(newForm);
          console.log('🛒 [CART-REC] Calling /cart/add.js...');

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData,
          });

          if (response.ok) {
            const data = await response.json();
            console.log('✅ [CART-REC] Successfully added to cart:', data);

            // Hide spinner, show checkmark briefly
            if (spinner) spinner.classList.add('hidden');
            if (plusIcon) {
              plusIcon.innerHTML =
                '<path d="M5 13l4 4L19 7" stroke="#dc600e" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>';
              plusIcon.style.display = 'block';
            }

            // Trigger cart update
            setTimeout(() => {
              const cartDrawerItems = document.querySelector('cart-drawer-items');
              if (cartDrawerItems) {
                cartDrawerItems.onCartUpdate();
              }

              // Reset button after cart updates
              setTimeout(() => {
                if (plusIcon) {
                  plusIcon.innerHTML =
                    '<path d="M10 4V16M4 10H16" stroke="#dc600e" stroke-width="2" stroke-linecap="round"/>';
                  plusIcon.style.display = 'block';
                }
                button.disabled = false;
                button.dataset.submitting = 'false';
              }, 1000);
            }, 300);
          } else {
            throw new Error('Failed to add to cart');
          }
        } catch (error) {
          console.error('Error adding to cart:', error);

          // Reset button state
          if (spinner) spinner.classList.add('hidden');
          if (plusIcon) plusIcon.style.display = 'block';
          button.disabled = false;
          button.dataset.submitting = 'false';

          alert('Failed to add item to cart. Please try again.');
        }
      };

      // Use capture phase to ensure we handle it first
      newForm.addEventListener('submit', handleSubmit, true);
    });
  }

  /**
   * Reset state (useful when drawer closes or cart state changes)
   */
  reset() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.isLoading = false;
    this.recommendationsLoaded = false; // Reset loaded state to allow fresh load
    this.lastCartHash = null; // Reset cart hash to detect changes
    console.log('🔄 Recommendations state reset');
  }
}

// Initialize global instance
window.cartComplementaryRecs = new CartComplementaryRecommendations();

// Listen for cart drawer events (cart state changed)
document.addEventListener('cart:drawer:refreshed', (event) => {
  console.log('🔄 Cart drawer refreshed, checking if reload needed');

  // Skip reload for gift-related events (gift checkbox, gift note updates)
  const source = event.detail?.source;
  if (source === 'gift-checkbox' || source === 'gift-note' || source === 'product-gift') {
    console.log('⏭️ Skipping recommendations reload for gift-related event:', source);
    return;
  }

  // Debounce to avoid rapid successive calls
  clearTimeout(window.cartComplementaryRecs.debounceTimer);
  window.cartComplementaryRecs.debounceTimer = setTimeout(() => {
    // This will only reload if cart hash changed
    window.cartComplementaryRecs.loadRecommendations(event.detail?.cartData);
  }, 300);
});

// Load recommendations on page load
document.addEventListener('DOMContentLoaded', () => {
  // Initial load of recommendations
  console.log('📄 Page loaded, loading initial recommendations');
  setTimeout(() => {
    window.cartComplementaryRecs.loadRecommendations();
  }, 500);

  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    // Don't reload on drawer open - recommendations already loaded
    // Only the cart:drawer:refreshed event will trigger reloads when cart changes

    // Reset abort controller when drawer closes (cleanup)
    const originalClose = cartDrawer.close.bind(cartDrawer);
    cartDrawer.close = function () {
      originalClose();
      // Don't reset isLoading or clear recommendations, just abort any pending requests
      if (window.cartComplementaryRecs.abortController) {
        window.cartComplementaryRecs.abortController.abort();
        window.cartComplementaryRecs.abortController = null;
      }
    };
  }
});

console.log('Cart Recommendations system loaded');
